import { markRaw } from 'vue';

// human.match.find ignores anything shorter
const MIN_DESCRIPTOR_LENGTH = 64;

// Stored shape: { v: 2, faces: [{ id, d: <base64 Float32Array> }] }. Version 1
// was the bare array the store used to persist: the whole employee payload plus
// one JSON number array per photo, ~13 KB per descriptor against ~5.5 KB here.
const STORAGE_VERSION = 2;

// Raised while the blob read from storage was still in the legacy format, so
// the faces service can rewrite it once instead of waiting for the next change.
export let legacyFacesLoaded = false;

export function markLegacyFacesUpgraded () {
	legacyFacesLoaded = false;
}

export function isDescriptor (descriptor) {
	return Array.isArray(descriptor) &&
		descriptor.length >= MIN_DESCRIPTOR_LENGTH &&
		typeof descriptor[0] === 'number';
}

/**
 * The in-memory face entry: the employee id and its first descriptor, and
 * nothing else. markRaw keeps Vue from proxying the entry, so neither the
 * persist watcher nor the matcher pays for reactivity over the 1024 floats.
 * Returns null when there is nothing the matcher could ever use.
 */
export function toFaceEntry (face) {
	const id = face?.id;
	const descriptor = face?.descriptors?.[0];

	if (!id || !isDescriptor(descriptor)) {
		return null;
	}

	return markRaw({ id, descriptors: [descriptor] });
}

export function encodeDescriptor (descriptor) {
	const bytes = new Uint8Array(Float32Array.from(descriptor).buffer);
	let binary = '';

	// chunked: String.fromCharCode cannot take the whole buffer as arguments
	for (let offset = 0; offset < bytes.length; offset += 0x8000) {
		binary += String.fromCharCode.apply(null, bytes.subarray(offset, offset + 0x8000));
	}

	return btoa(binary);
}

export function decodeDescriptor (encoded) {
	const binary = atob(encoded);

	if (binary.length % 4 !== 0 || binary.length < MIN_DESCRIPTOR_LENGTH * 4) {
		throw new Error(`[faceEntries] Invalid descriptor payload (${binary.length} bytes)`);
	}

	const bytes = new Uint8Array(binary.length);

	for (let index = 0; index < binary.length; index++) {
		bytes[index] = binary.charCodeAt(index);
	}

	return Array.from(new Float32Array(bytes.buffer));
}

export function serializeFaces (faces) {
	const entries = [];

	for (const face of faces ?? []) {
		const descriptor = face?.descriptors?.[0];

		if (!face?.id || !isDescriptor(descriptor)) continue;

		entries.push({ id: face.id, d: encodeDescriptor(descriptor) });
	}

	return JSON.stringify({ v: STORAGE_VERSION, faces: entries });
}

export function deserializeFaces (text) {
	if (!text) return undefined;

	const parsed = JSON.parse(text);

	// legacy blob: slim every entry on the way in
	if (Array.isArray(parsed)) {
		legacyFacesLoaded = true;

		return parsed
			.map(face => toFaceEntry(face))
			.filter(Boolean);
	}

	if (parsed?.v === STORAGE_VERSION && Array.isArray(parsed.faces)) {
		const entries = [];

		for (const face of parsed.faces) {
			try {
				const entry = toFaceEntry({ id: face?.id, descriptors: [decodeDescriptor(face?.d)] });

				if (entry) entries.push(entry);
			} catch (error) {
				console.warn('[faceEntries] Dropping unreadable face', face?.id, error);
			}
		}

		return entries;
	}

	// unknown shape: the store falls back to its default and the service's
	// "empty faces with a lastUpdate" check forces a regeneration
	return undefined;
}
